import { LitElement, html, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import { styleMap } from "lit-html/directives/style-map"
import { stop } from "../utils"
import commonStyle from "../common.css"
import style from "./modal.css"

window.bui = window.bui || {}
/**
 * @type {Modal[]}
 */
const modalStack = []
window.bui.modalStack = modalStack
/**
 * 現在表示している最前面のモーダルのz-indexを取得する
 */
window.bui.getMaxModalZindex = () => {
	return Math.max(...modalStack.map(modal => modal.actualZIndex), 100)
}

// ESCキーで最上面のモーダルを消す
window.addEventListener("keydown", (e) => {
	if (e.key === "Escape" && modalStack.length > 0) {
		modalStack[modalStack.length - 1].hide(false)
	}
})

export default class Modal extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			persistent: { type: Boolean },
			absolute: { type: Boolean },
			zIndex: { type: Number, attribute: "z-index" },
			transparent: { type: Boolean },
			white: { type: Boolean }
		}
	}

	/**
	 * @type {boolean} フェードアウトアニメーション中か
	 */
	#hiding = false
	/**
	 * @type {boolean} モーダルを表示中か
	 */
	#show = false
	/**
	 * @type {number} モーダルのz-index
	 */
	#zIndex = 100

	/**
	 * @type {HTMLElement} モーダル要素
	 */
	#modalEl = null

	constructor () {
		super()
		/**
		 * @type {boolean} trueの場合クリック時の自動モーダル解除を抑止
		 */
		this.persistent = false
		/**
		 * @type {boolean} position属性をfixedではなくabsoluteにする
		 */
		this.absolute = false
		/**
		 * モーダルを透明にする
		 */
		this.transparent = false
		/**
		 * 背景を白にする
		 */
		this.white = false
	}

	render () {
		return html`
			<div class=${this.class} style=${this.style} @click="${e => { this.#hideIfModalClicked(e) }}" @contextmenu="${e => { e.preventDefault() }}">
					<slot @modal-beforehide="${stop}"></slot>
			</div>
		`
	}

	firstUpdated () {
		this.#modalEl = this.shadowRoot.querySelector(".modal")
	}

	get class () {
		return classMap({
			modal: true,
			"is-show": this.#show,
			"is-hiding": this.#hiding,
			"is-hide": !this.#show && !this.#hiding,
			"is-absolute": this.absolute,
			"is-transparent": this.transparent,
			"is-white": this.white
		})
	}

	get style () {
		return styleMap({
			"z-index": this.#zIndex
		})
	}

	get isShow () {
		return this.#show
	}

	get actualZIndex () {
		return this.#zIndex
	}

	show () {
		if (this.#show) return Promise.resolve
		this.#show = true
		if (this.zIndex == null) {
			// z-indexが指定されていない場合、現在表示中のスタックから最大値を取得する
			this.#zIndex = window.bui.getMaxModalZindex() + 1
			modalStack.push(this)
		} else {
			// z-indexが指定されている場合、そのまま表示
			this.#zIndex = this.zIndex
		}
		return this.requestUpdate().then(() => {
			const tryFireAnimationEndEvent = () => {
				this.shadowRoot.querySelector(".modal").addEventListener("animationend", e => {
					if (e.target === e.currentTarget) {
						this.dispatchEvent(new CustomEvent("modal-aftershow", {
							// shadow DOMの境界を通してバブリングするようにする
							bubbles: true,
							composed: true
						}))
					} else {
						tryFireAnimationEndEvent()
					}
				}, { once: true })
			}
			tryFireAnimationEndEvent()
		})
	}

	/**
	 *
	 * @returns {boolean} true if closed
	 */
	hide (force = true) {
		if (!this.#show) return false
		if (this.persistent && !force) return !this.#show
		const idx = modalStack.indexOf(this)

		// スタックで管理していないモーダルはそのままクローズ
		if (idx === -1) {
			return this.#hideCore(true)
		}

		if (modalStack.length === idx + 1) {
			// 自身が最上面のモーダルの場合、自身を閉じる
			const isClosed = this.#hideCore(true)
			if (isClosed) modalStack.splice(-1, 1)
			return isClosed
		} else {
			// 自分より上のモーダルを閉じる

			// 自身より上のモーダル取得
			const removedModals = modalStack.splice(idx)
			let cancelled = false
			// 上から順に閉じる。キャンセルされたらそれ以降のモーダルは閉じずにスタックに詰めなおす
			const tempList = []
			removedModals.reverse().forEach(modal => {
				if (!cancelled) {
					cancelled = !modal.#hideCore(true, true)
				}
				if (cancelled) {
					tempList.push(modal)
				}
			})
			tempList.reverse().forEach(modal => {
				modalStack.push(modal)
			})
			return !cancelled
		}
	}

	/**
	 *
	 * @returns {boolean} true if closed
	 */
	#hideCore () {
		// 閉じるイベントを発火し、preventDefaultされたら閉じない
		const isCancelled = !this.dispatchEvent(new CustomEvent("modal-beforehide", { cancelable: true }))
		if (isCancelled) return false

		// 以下、閉じる処理
		this.#hiding = true
		this.#show = false

		// フェードアウトアニメーションが終わったらアニメーション用のクラスを取り除く
		this.shadowRoot.querySelector(".modal").addEventListener("animationend", () => {
			this.#hiding = false
			this.requestUpdate()

			this.dispatchEvent(new CustomEvent("modal-afterhide", {
				// shadow DOMの境界を通してバブリングするようにする
				bubbles: true,
				composed: true
			}))
		}, { once: true })
		this.requestUpdate()
		this.dispatchEvent(new CustomEvent("modal-hide", {
			// shadow DOMの境界を通してバブリングするようにする
			bubbles: true,
			composed: true
		}))
		return true
	}

	/**
	 * クリックされた要素がモーダルなら閉じる
	 * @param {Event} e
	 */
	#hideIfModalClicked (e) {
		if (e.target === this.#modalEl) {
			this.hide(false)
		}
	}
}
